import React, { useEffect, useState } from "react";
import "../My Projects/my_projects.css";
import { toast } from "react-toastify";
import {
  getAllAssignedProjects,
  getReport,
} from "../../service/project_service";
import { useNavigate } from "react-router-dom";
import CustomLoadingindicator from "../../components/custom_loading";

const MyProjects = () => {
  const [projects, setProjects] = useState([]);
  const [workReports, setWorkReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const getProjectStats = (project) => {
    const projectReports = workReports.filter(
      (report) =>
        report.projectid === project.projectid ||
        report.projectname === project.projectname,
    );

    // Calculate total minutes correctly handling HH.mm format (e.g., 3.20 = 3h 20m)
    const totalMinutes = projectReports.reduce((total, report) => {
      const val = Number(report.workinghours) || 0;
      const hours = Math.floor(val);
      const minutes = Math.round((val - hours) * 100);
      return total + hours * 60 + minutes;
    }, 0);

    // Calculate total unique working days
    const uniqueDays = new Set(projectReports.map((r) => r.workdate)).size;

    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    return {
      formattedHours: `${hours} hr: ${minutes} mins`,
      formattedDays: `${uniqueDays} ${uniqueDays > 1 ? "days" : "day"}`,
    };
  };

  const getAllMyProjectsData = async () => {
    try {
      const [projectsResponse, reportsResponse] = await Promise.all([
        getAllAssignedProjects(),
        getReport({
          startDate: "",
          endDate: "",
          projectid: 0,
        }),
      ]);

      if (projectsResponse?.status === "success" && projectsResponse.data) {
        setProjects(projectsResponse.data);
      } else {
        toast.error(projectsResponse?.message || "Failed to fetch projects");
      }

      if (reportsResponse?.status === "success") {
        setWorkReports(reportsResponse.data || []);
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
          error?.message ||
          "Something went wrong",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getAllMyProjectsData();
  }, []);

  if (loading) {
    return <CustomLoadingindicator />;
  }

  return (
    <div className="projects-container">
      <div className="page-header">
        <h2>My Assigned Projects</h2>
        <p className="page-subtitle">
          View and manage your assigned projects and work logs
        </p>
      </div>

      {projects.length === 0 ? (
        <div className="empty-projects-state">
          <img
            src="https://cdn-icons-png.flaticon.com/128/14782/14782500.png"
            alt="No-Project"
            height={50}
            width={50}
            style={{ marginBottom: "10px" }}
          />
          <h3>No Projects Assigned Yet</h3>
        </div>
      ) : (
        <div className="projects-grid">
          {projects.map((project) => {
            const stats = getProjectStats(project);
            const firstModule = project.modules?.[0] || {};

            return (
              <div
                className="project-card"
                key={project.projectid}
                onClick={() =>
                  navigate("/project-detail", {
                    state: { project, totalStats: stats },
                  })
                }
                style={{ cursor: "pointer" }}
              >
                <div className="card-header">
                  <div className="header-project-title">
                    <h3>{project?.projectname || "Untitled Project"}</h3>
                    <span className="project-code">
                      Modules:{" "}
                      {project.modules?.map((m) => m.modulename).join(", ") ||
                        "No modules"}
                    </span>
                  </div>
                  <span
                    className={`priority ${(firstModule.priority || "medium").toLowerCase()}`}
                  >
                    {firstModule.priority || "Medium"}
                  </span>
                </div>

                <div className="card-body">
                  <div className="info-row">
                    <span className="info-value">
                      {project.description ||
                        "No project description provided."}
                    </span>
                  </div>
                  <div style={{ height: "10px" }}></div>
                  <div className="card-dates">
                    <div className="date-item">
                      <span className="date-label">Assigned Date</span>
                      <span className="date-value">
                        {firstModule.assigneddate
                          ? new Date(
                              firstModule.assigneddate,
                            ).toLocaleDateString("en-GB")
                          : "N/A"}
                      </span>
                    </div>
                    <div className="date-item">
                      <span className="date-label">Latest Deadline</span>
                      <span className="date-value deadline-value">
                        {project.modules?.length > 0
                          ? new Date(
                              Math.max(
                                ...project.modules.map(
                                  (m) => new Date(m.deadlinedate),
                                ),
                              ),
                            ).toLocaleDateString("en-GB")
                          : "No Deadline"}
                      </span>
                    </div>
                  </div>

                  <div className="worked-info-container">
                    <div className="worked-item-card">
                      <div className="icon-wrapper">📅</div>
                      <div className="stats-info">
                        <div className="stats-label">Total Worked Days</div>
                        <div className="stats-value">{stats.formattedDays}</div>
                      </div>
                    </div>
                    <div className="worked-item-card">
                      <div className="icon-wrapper">⏰</div>
                      <div className="stats-info">
                        <div className="stats-label">Total Worked Hours</div>
                        <div className="stats-value">
                          {stats.formattedHours}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="card-footer">
                  <button
                    className="add-report-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate("/add-workreports", {
                        state: {
                          projectid: project?.projectid,
                        },
                      });
                    }}
                  >
                    Add Report
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MyProjects;
